import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, mixins, setFontVariant } from "@similarweb/styles";
import {
    Autocomplete,
    Textfield,
    ListItemSeparator,
    ListItemWebsite,
    ListItemWebsitePartialSelection,
    QueryContainer,
} from "@similarweb/ui-components";

import type { PropsOf } from "app/helpers/types/react";

import type { IAutocompleteStyledProps } from "./types";

const resolveAutocompleteWidth = (width?: string | number, defaultWidth = "248px") => {
    if (!width) {
        return defaultWidth;
    }

    const isPercent = width.toString().endsWith("%");
    const isPixel = width.toString().endsWith("px");
    if (isPercent || isPixel) {
        return width;
    }

    return `${width}px`;
};

// The props are necessary for a responsive design.
export const AutocompleteStyled = styled(Autocomplete)<IAutocompleteStyledProps>`
    width: ${({ width }) => resolveAutocompleteWidth(width)};
    max-height: 380px;
    position: ${({ position }) => (position ? position : "absolute")};
    ${({ zIndex = 1030 }) => `z-index:${zIndex}`};
    left: 0;
    top: ${({ top = -4 }) => `${top}px`};

    .ListItemsContainer {
        box-shadow: 0 0 8px 6px -6px rgba(0, 0, 0, 0.24);
    }

    .ItemIcon {
        flex: none;
    }

    .ItemText {
        ${mixins.truncateText()};
    }
`;

export const MainSiteAutocompleteStyled = styled(AutocompleteStyled)<IAutocompleteStyledProps>`
    .ItemText {
        ${mixins.setFontVariant("body2", colorsPalettes.carbon["500"])};
    }
`;

export const StyledSecondColumn = styled.div`
    text-align: center;
    flex: 2;
`;

const StyledSecondColumnRightAlign = styled(StyledSecondColumn)`
    text-align: right;
`;

export const StyledItemWrapper = styled.div`
    display: flex;
    align-items: center;

    &.active {
        background-color: ${colorsPalettes.bluegrey["200"]};
    }

    &.disabled {
        opacity: 0.5;
        pointer-events: none;
    }
`;

export const NoDataWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const NoDataIcon = styled(SWReactIcons)`
    height: 24px;
    width: 24px;
    margin: 16px 0 8px;
`;

export const NoDataText = styled.div`
    font-size: 16px;
    margin-bottom: 40px;
    max-width: 44%;
    color: ${colorsPalettes.midnight[100]};
    text-align: center;
`;

export const StyledTextField = styled(Textfield)`
    width: 100%;
`;

export const ListExamplesHeader = styled.div`
    ${setFontVariant("input2", colorsPalettes.carbon["400"])}
    padding: 12px 0 16px 16px;
    a {
        color: currentcolor;
        text-decoration: underline;
        cursor: pointer;
    }
`;

export const QueryResultsWrapper = styled.div`
    margin-bottom: 12px;
`;

export const ListItemWebsitePartialSelectionStyled = styled(ListItemWebsitePartialSelection)<
    PropsOf<typeof ListItemWebsitePartialSelection> & {
        content?: string;
        reverseEllipsis?: boolean;
    }
>`
    color: ${colorsPalettes.carbon[500]};

    ${QueryContainer} {
        ${({ reverseEllipsis }) => reverseEllipsis && "direction: rtl;"}
        &::before {
            content: ${({ content }) => content && `"${content}"`};
            unicode-bidi: plaintext;
            color: ${colorsPalettes.carbon[200]};
        }
    }
    padding: 8px 16px;
`;

export const ListItemWebsiteStyled = styled(ListItemWebsite)`
    padding: 8px 16px;
`;

export const ItemWebsitePartialSelectionStyled = styled(ListItemWebsitePartialSelection)<
    PropsOf<typeof ListItemWebsitePartialSelection> & {
        content?: string;
        reverseEllipsis?: boolean;
    }
>`
    color: transparent;
    width: 100%;

    ${QueryContainer} {
        ${({ reverseEllipsis }) => reverseEllipsis && "direction: rtl;"}
        &::after {
            content: ${({ content }) => content && `"${content}"`};
            unicode-bidi: plaintext;
            color: ${colorsPalettes.carbon[200]};
        }
    }
`;

export const Clear = styled.div`
    margin-right: 16px;
    cursor: pointer;
`;

export const CustomIndicator = styled.div`
    ${setFontVariant("input2", colorsPalettes.carbon[200])}
    white-space: nowrap;
`;

export const ListItemSeparatorStyled = styled(ListItemSeparator)`
    padding: 12px 16px;
`;
